import React, { useEffect, useState, useRef } from 'react'
import cx from 'classnames'
import vsbl from 'vsbl'
import BlockContent from 'src/components/global/BlockContent'

import { Image } from 'src/components/image'
import { Serializer } from 'src/utils/serializer'

export interface TextAndMediaProps {
  data: {
    text: any[]
    backgroundColor: string
    mediaType?: 'image' | 'video'
    image?: {
      asset: {
        _id: string
        _ref?: string
      }
    }
    mobileImage?: {
      asset: {
        _id: string
        _ref?: string
      }
    }
    // Video fields
    vimeoVideo?: string
    youtubeVideo?: string
    videoPosterImage?: {
      asset: {
        _id: string
        _ref?: string
      }
    }
    autoPlay?: boolean
    aspectRatio?: string
    videoDisplay?: 'fullWidth' | 'container'
    textAboveMedia?: boolean
    textAboveImage?: boolean
    alignment?: string
    spacing: string
    style: string
  }
}

export const TextAndMedia = ({ data }: TextAndMediaProps) => {
  const {
    text,
    backgroundColor,
    mediaType = 'image',
    image,
    alignment,
    spacing,
    style,
    mobileImage,
    textAboveImage,
    textAboveMedia,
    vimeoVideo,
    youtubeVideo,
    videoPosterImage,
    autoPlay = false,
    aspectRatio = '16:9',
    videoDisplay = 'container',
  } = data

  const textAbove = textAboveMedia || textAboveImage || false

  const dom = useRef<HTMLDivElement>(null)
  const [className, setClass] = useState('')
  const [_playing, setPlaying] = useState(autoPlay)
  const [showPoster, setShowPoster] = useState(!autoPlay)

  useEffect(() => {
    const { current } = dom
    const vs = vsbl(current)(() => {
      setClass('is-visible')

      if (mediaType === 'video' && autoPlay) {
        setPlaying(true)
        setShowPoster(false)
      }
    })
    vs.update()
    return () => {
      vs.destroy()
    }
  }, [autoPlay, mediaType])

  const desktopImageIdentifier = image?.asset?._id || image?.asset?._ref
  const mobileImageIdentifier =
    mobileImage?.asset?._id || mobileImage?.asset?._ref
  const videoPosterImageIdentifier =
    videoPosterImage?.asset?._id || videoPosterImage?.asset?._ref

  const formatYoutubeUrl = (url: string, autoplay: boolean = false) => {
    if (!url) return ''

    let videoId = ''
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
    const match = url.match(regExp)

    if (match && match[2].length === 11) {
      videoId = match[2]
    } else {
      return url
    }

    return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1${
      autoplay ? '&autoplay=1&mute=1' : ''
    }`
  }

  const formatVimeoUrl = (url: string, autoplay: boolean = false) => {
    if (!url) return ''

    let videoId = ''
    const regExp = /^.*(vimeo.com\/)((channels\/[A-Za-z]+\/)|(groups\/[A-Za-z]+\/videos\/))?([0-9]+)/
    const match = url.match(regExp)

    if (match && match[5]) {
      videoId = match[5]
    } else {
      return url
    }

    return `https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0${
      autoplay ? '&autoplay=1&muted=1' : ''
    }`
  }

  const getAspectRatioStyle = () => {
    switch (aspectRatio) {
      case '4:3':
        return { paddingBottom: '75%' }
      case '1:1':
        return { paddingBottom: '100%' }
      case '9:16':
        return { paddingBottom: '177.78%' }
      default:
        return { paddingBottom: '56.25%' }
    }
  }

  const handlePosterClick = () => {
    setShowPoster(false)
    setPlaying(true)
  }

  const renderMedia = () => {
    if (mediaType === 'video') {
      const hasPoster = !!videoPosterImageIdentifier
      const shouldShowVideo = !hasPoster || (hasPoster && !showPoster)
      const shouldAutoplay = autoPlay || (hasPoster && !showPoster)

      return (
        <div
          className={cx('video__wrapper x', {
            'rounded-corners': true,
            'video-full-width': videoDisplay === 'fullWidth',
            'video-container': videoDisplay === 'container',
          })}
          style={getAspectRatioStyle()}
        >
          {hasPoster && showPoster && (
            <div
              className="video-poster-overlay"
              onClick={handlePosterClick}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 10,
                cursor: 'pointer',
                overflow: 'hidden',
                borderRadius: 'inherit',
              }}
            >
              <Image
                imageId={videoPosterImageIdentifier}
                alt="Video thumbnail"
                rawStyleOverride={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center center',
                }}
              />
              <div
                className="play-button"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    width: 0,
                    height: 0,
                    borderTop: '15px solid transparent',
                    borderBottom: '15px solid transparent',
                    borderLeft: '25px solid white',
                    marginLeft: '8px',
                  }}
                ></div>
              </div>
            </div>
          )}

          {shouldShowVideo && (
            <>
              {youtubeVideo ? (
                <iframe
                  src={formatYoutubeUrl(youtubeVideo, shouldAutoplay)}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="video-iframe"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: 'inherit',
                  }}
                  title="YouTube video"
                />
              ) : vimeoVideo ? (
                <iframe
                  src={formatVimeoUrl(vimeoVideo, shouldAutoplay)}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  className="video-iframe"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: 'inherit',
                  }}
                  title="Vimeo video"
                />
              ) : (
                <div className="video-placeholder">
                  No video source provided
                </div>
              )}
            </>
          )}
        </div>
      )
    }

    return (
      <>
        <div className="show__desktop">
          {desktopImageIdentifier && (
            <Image imageId={desktopImageIdentifier} alt="" />
          )}
        </div>
        <div className="show__mobile">
          {mobileImageIdentifier && (
            <Image imageId={mobileImageIdentifier} alt="" />
          )}
          {!mobileImageIdentifier && desktopImageIdentifier && (
            <Image imageId={desktopImageIdentifier} alt="" />
          )}
        </div>
      </>
    )
  }

  return (
    <div
      ref={dom}
      className={cx('image__text', className, backgroundColor, spacing, {
        cw: backgroundColor === 'bcb',
      })}
    >
      <div
        className="container--l outer mxa al x mobile__flex"
        style={{ flexDirection: textAbove ? 'column-reverse' : 'column' }}
      >
        <div
          className={cx('f jcb fw ', alignment, style, {
            jcs: style === 'card' && alignment !== 'jcrr',
            aic: style !== 'card',
            aist: style === 'card',
          })}
        >
          <div
            className={cx('x rel animate opacity d2 slide-up image__text-img', {
              'col-l-5': style === 'card',
              'col-m-4': style !== 'card',
            })}
          >
            {renderMedia()}
          </div>
          <div
            className={cx(
              'x animate opacity d3 slide-up image__text-content show__desktop',
              {
                'col-l-4 p1': style === 'card',
                'col-m-6': style !== 'card',
              }
            )}
          >
            <BlockContent blocks={text} serializers={Serializer} />
          </div>
        </div>
        <div
          className={cx(
            'x animate opacity d3 slide-up image__text-content show__mobile',
            {
              'col-l-4 p1': style === 'card',
              'col-m-6': style !== 'card',
            }
          )}
        >
          <BlockContent blocks={text} serializers={Serializer} />
        </div>
      </div>
    </div>
  )
}

export default TextAndMedia
